import * as React from "react";

import { Box, Link, makeStyles, Theme, Typography } from "@material-ui/core";

import valuePropIcon1 from "client/assets/cash-value-props-icon1.svg";
import valuePropIcon2 from "client/assets/cash-value-props-icon2.svg";
import valuePropIcon3 from "client/assets/cash-value-props-icon3.svg";
import { useTranslation } from "client/hooks";
import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";
import { logAmplitudeEvent } from "client/lib/amplitude";

export const CASH_PROGRAM_RULES_LINK =
  "https://developers.cash.app/docs/api/resources/program-rules";

const useStyles = makeStyles((theme: Theme) => {
  const { gray60 } = useNormalizedBrandPaletteValues(theme);

  return {
    grayUnderlineLink: {
      color: gray60,
      textDecoration: "underline",
    },
    summary: {
      color: theme.palette.common.white,
      lineHeight: 1.4,
      marginBottom: theme.spacing(2),
    },
    factBody: {
      fontSize: `${14 / 16}rem`,
      color: theme.palette.common.white,
      lineHeight: 1.4,
    },
    factHeader: {
      fontSize: `${24 / 16}rem`,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.common.white,
      lineHeight: 1.4,
    },
    dataFactWrapper: {
      display: "flex",
      [theme.breakpoints.up("md")]: {
        maxWidth: "60%",
        width: "60%",
      },
      [theme.breakpoints.down("sm")]: {
        margin: theme.spacing(3, 3, 3),
      },
    },
    dataFactsWrapper: {
      backgroundColor: theme.palette.common.black,
      height: "100%",
      [theme.breakpoints.down("sm")]: {
        alignItems: "flex-start",
      },
    },
    footer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      [theme.breakpoints.up("md")]: {
        maxWidth: "60%",
        width: "60%",
      },
      [theme.breakpoints.down("sm")]: {
        margin: theme.spacing(3, 3, 3),
      },
    },
    footerItem: {
      fontSize: `${10 / 16}rem`,
      lineHeight: 1.4,
      color: gray60,
      marginBottom: theme.spacing(2),
    },
  };
});

export const CashPayValueProp: React.FC<{
  icon: React.ReactNode;
  body: string | React.ReactNode;
  header: string;
}> = ({ header, body, icon }) => {
  const classes = useStyles();
  return (
    <Box className={classes.dataFactWrapper} mb={6}>
      <Box mr={3}>{icon}</Box>
      <Box>
        <Typography variant="h5" className={classes.factHeader}>
          {header}
        </Typography>
        <Typography className={classes.factBody} variant="body2">
          {body}
        </Typography>
      </Box>
    </Box>
  );
};

export const CashPayValueProps: React.FC = () => {
  const t = useTranslation();
  const classes = useStyles();

  const valueProps = [
    {
      iconSrc: valuePropIcon1,
      headerText: t("uiCashPayValuePropFactHeader1"),
      bodyText: t("uiCashPayValuePropFactBody1"),
    },
    {
      iconSrc: valuePropIcon2,
      headerText: t("uiCashPayValuePropFactHeader2"),
      bodyText: t("uiCashPayValuePropFactBody2"),
    },
    {
      iconSrc: valuePropIcon3,
      headerText: t("uiCashPayValuePropFactHeader3"),
      bodyText: t("uiCashPayValuePropFactBody3"),
    },
  ];

  return (
    <Box
      alignItems="center"
      className={classes.dataFactsWrapper}
      display="flex"
      flexDirection="column"
      justifyContent="center"
    >
      <Box
        className={classes.dataFactWrapper}
        mb={2}
        display="flex"
        flexDirection="column"
      >
        <Typography className={classes.summary} variant="body2">
          Afterpay is giving your customers a brand new way to check out with
          Cash App Pay.
        </Typography>
        <Typography className={classes.summary} variant="body2">
          Cash App is the <strong>#1 finance app</strong> in the US app store,
          attracting <strong>51M+ monthly transacting actives*</strong> looking
          for an easier way to send, spend, save and invest their money.
        </Typography>
      </Box>
      {valueProps.map(({ iconSrc, headerText, bodyText }, i) => (
        <CashPayValueProp
          icon={<img src={iconSrc} alt={`icon${i}`} />}
          header={headerText}
          body={bodyText}
          key={`value-prop-${i}`}
        />
      ))}
      <Box className={classes.footer} mt={4}>
        <Typography className={classes.footerItem}>
          * As of March 2023. A transacting active is a Cash App account that
          has at least one financial transaction using any product or service
          within Cash App during a specified period.
        </Typography>

        <Typography className={classes.footerItem}>
          ** Cash App assumes liability for consumer fraud as covered by
          Regulation E.{" "}
          <Link
            className={classes.grayUnderlineLink}
            href={CASH_PROGRAM_RULES_LINK}
            rel="noopener noreferrer"
            target="_blank"
            onClick={() => {
              logAmplitudeEvent("CASH_FORM", "clicked_cash_program_rules_link");
            }}
          >
            Some exclusions apply.
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};
